import _ from 'underscore';

import moment from "moment";
import momentLocaleEn from "moment/locale/en-gb";
import momentLocaleEt from "moment/locale/et";
import momentLocaleFi from "moment/locale/fi";
import momentLocaleSv from "moment/locale/sv";
import momentLocaleLt from "moment/locale/lt";
import momentLocaleLv from "moment/locale/lv";
import momentLocaleRu from "moment/locale/ru";

import alt from '../alt';

import config from '../../config';
import Log from '../../Log';
import LocaleActions from './LocaleActions';

import * as localeData from '../../data/locale';

/**
 * @typedef {Object} LocaleData
 * @property {string} locale Locale code
 * @property {string} lang Locale language
 * @property {string} langEn Locale language in english
 * @property {Object.<string,string>} trans Dictionary of string translations from english to local language
 */

/**
 * @typedef {Object} LocaleStoreState
 * @property {Object.<string,LocaleData>} locales Locale info, mapping from locale code to locale data
 * @property {string} current Current locale code
 */
/**
 *
 */
class LocaleStore {
    constructor() {
        this.state = {
            /**
             * Locale data for all locales represented as mapping from locale code to locale data
             * @type {Object.<string,LocaleData>}
             */
            locales: this.getLocales(),
            /**
             * Current locale code
             * @type {string}
             */
            current: config.defaultLocale
        };
        moment.locale(this.state.current);
        this.bindActions(LocaleActions);
        this.exportPublicMethods({
            trans: this.trans,
            formatCurrency: this.formatCurrency,
            formatDecimal: this.formatDecimal,
            formatDate: this.formatDate,
            formatTime: this.formatTime,
            formatDateTime: this.formatDateTime,
            formatElapsedTime: this.formatElapsedTime
        });
    }

    /**
     * Returns locale data for all locales
     * @returns {Object.<string,LocaleData>}
     */
    getLocales() {
        let locales = {};
        for (let l of _.allKeys(localeData)) {
            locales[localeData[l].locale] = localeData[l];
        }
        return locales;
    }

    onSetLocale(lang) {
        if (_.has(this.state.locales, lang)) {
            this.setState({current: lang});
            moment.locale(lang);
        }
    }

    trans(msg) {
        try {
            if (_.has(this.state.locales, this.state.current)) {
                var trans = this.state.locales[this.state.current].trans;
                if (trans && _.has(trans, msg)) return trans[msg];
            }
        } catch (e) {
            Log.error('Error translating message: ' + e.toString());
        }
        return msg;
    }

    formatCurrency(value, currencyCode) {
        if (value == null) return "";
        value = Number(value);
        let props = {
            style: "currency",
            currency: currencyCode,
            currencyDisplay: "code"
        };
        //display output
        let s = value.toLocaleString(this.state.current, props);
        // Move currency symbol to the end:
        return s.replace(/^([^-0-9 ]+) *([-0-9].*)$/, "$2 $1");

    }

    formatDecimal(value, precision) {
        if (value == null) return "";
        value = Number(value);
        let props = {
            style: "decimal",
            minimumFractionDigits: precision
        };
        //display output
        return value.toLocaleString(this.state.current, props);

    }

    /**
     * Format date
     * @param {string} value Full date and time in ISO format or only date in ISO format
     * @returns {string} Date in current locale format
     */
    formatDate(value) {
        if (value == null) return "";
        let date = new Date(value);
        let result = date.toLocaleDateString(this.state.current);
        return result.replace(/\b([0-9])\b/gi, "0$1");
    }

    /**
     * Format time
     * @param {string} value Full date and time in ISO format or only time in ISO format
     * @returns {string} Time in current locale format
     */
    formatTime(value) {
        if (value == null) return "";
        let idx = value.indexOf("T");
        if (idx == -1) {
            let currentDate = new Date();
            let dateString = currentDate.toISOString();
            idx = dateString.indexOf("T");
            value = dateString.substring(0, idx+1) + value;
        }
        if (value.indexOf("+") == -1 && value.indexOf("Z") == -1) value += "Z";

        let date = new Date(value);
        return date.toLocaleTimeString(this.state.current);
    }

    /**
     * Format date and time
     * @param {string} value Full date and time in ISO format
     * @returns {string} Date and time in current locale format
     */
    formatDateTime(value) {
        if (value == null) return "";
        let date = new Date(value);
        let result = date.toLocaleString(this.state.current);
        return result.replace(/\b([0-9])\b/gi, "0$1");
    }

    formatElapsedTime(value, value2) {
        if (value == null) return "";
        if (value2 != null) return moment(value).from(value2);
        return moment(value).fromNow();
    }
}

export default alt.createStore(LocaleStore, 'LocaleStore');
